import { boothLog } from './telemetry/boothLogClient'
import { isBoothSessionActive } from '@renderer/stores/boothEventStore'

/**
 * A booth renderer that outlived a deploy.
 *
 * The booth UI is served from the web and its code is split into hashed files.
 * A booth that started before a deploy keeps the old bundle, and the first time
 * it lazily loads the camera library afterwards it asks for the previous
 * build's file, which the server no longer has: "Gagal menyambungkan kamera:
 * Failed to fetch dynamically imported module …/libapi-<hash>.js". Nothing
 * reloaded the booth, so the camera stayed down until someone restarted it.
 *
 * Reloading is only safe when (1) a newer build really is live, which also
 * proves the server is reachable (an offline booth must never reload: its UI
 * comes from the web), and (2) no guest is in the middle of a session.
 */

/** The entry script a page loads, as a path: `/assets/main-<hash>.js`. */
export function entryScriptIn(html: string): string | null {
  const match = html.match(/<script[^>]+type="module"[^>]+src="([^"]*\/assets\/main-[\w-]+\.js)"/i)
  return match ? toPath(match[1]) : null
}

function toPath(src: string): string {
  try {
    return new URL(src, 'http://local').pathname
  } catch {
    return src
  }
}

/** The entry script this renderer is running, or null outside a production build. */
export function currentEntryScript(doc: Pick<Document, 'querySelector'> = document): string | null {
  const script = doc.querySelector('script[type="module"][src*="/assets/main-"]')
  const src = script?.getAttribute('src')
  return src ? toPath(src) : null
}

const INDEX_TIMEOUT_MS = 5_000

/**
 * True only when the server's index names a different entry script than the
 * one running here. Unknown on either side (offline, timeout, dev build) is
 * false, so nothing is reloaded on a guess.
 */
export async function isNewerBuildLive(
  deps: {
    fetchIndex?: () => Promise<string>
    current?: string | null
  } = {}
): Promise<boolean> {
  const current = deps.current !== undefined ? deps.current : currentEntryScript()
  if (!current) return false
  const fetchIndex =
    deps.fetchIndex ??
    (async (): Promise<string> => {
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), INDEX_TIMEOUT_MS)
      try {
        const res = await fetch(`${window.location.origin}/`, {
          cache: 'no-store',
          signal: controller.signal
        })
        if (!res.ok) throw new Error(`index ${res.status}`)
        return await res.text()
      } finally {
        clearTimeout(timer)
      }
    })
  try {
    const live = entryScriptIn(await fetchIndex())
    return !!live && live !== current
  } catch {
    return false
  }
}

/** The project's start screen (`/<projectId>`), where no guest flow is running. */
export function isWelcomeRoute(pathname: string): boolean {
  return /^\/[0-9a-f]{24}\/?$/i.test(pathname)
}

const RELOAD_MARK_KEY = 'dreambooth:staleBuildReloadAt'
/** A second stale-build reload this soon after the last one means it did not help. */
const RELOAD_COOLDOWN_MS = 10 * 60_000
const IDLE_POLL_MS = 15_000

interface ReloadDeps {
  isIdle: () => boolean
  reload: () => void
  storage: Pick<Storage, 'getItem' | 'setItem'> | null
  now: () => number
  every: (fn: () => void, ms: number) => unknown
  stop: (handle: unknown) => void
}

let scheduled = false

const defaultDeps = (): ReloadDeps => ({
  isIdle: () => isWelcomeRoute(window.location.pathname) && !isBoothSessionActive(),
  reload: () => window.location.reload(),
  storage: ((): Pick<Storage, 'getItem' | 'setItem'> | null => {
    try {
      return window.sessionStorage
    } catch {
      return null
    }
  })(),
  now: () => Date.now(),
  every: (fn, ms) => setInterval(fn, ms),
  stop: (handle) => clearInterval(handle as ReturnType<typeof setInterval>)
})

/**
 * Reload the booth onto the live build at the next idle moment. One schedule at
 * a time, and never twice within the cooldown (sessionStorage survives the
 * reload), so a build that keeps failing cannot put the booth in a loop.
 * Returns whether a reload was scheduled.
 */
export function scheduleReloadWhenIdle(
  reason: string,
  overrides: Partial<ReloadDeps> = {}
): boolean {
  if (scheduled) return false
  const deps = { ...defaultDeps(), ...overrides }
  let last = 0
  try {
    last = Number(deps.storage?.getItem(RELOAD_MARK_KEY) ?? 0) || 0
  } catch {
    // Storage blocked: no cooldown record, reload at most once per page anyway.
  }
  if (last && deps.now() - last < RELOAD_COOLDOWN_MS) {
    boothLog.log({
      level: 'warn',
      category: 'update',
      event: 'stale_build.reload_skipped',
      message:
        'A newer booth build is live but this booth reloaded for it recently; not reloading again',
      data: { reason, lastReloadAgoMs: deps.now() - last }
    })
    return false
  }
  scheduled = true
  boothLog.log({
    level: 'warn',
    category: 'update',
    event: 'stale_build.reload_scheduled',
    message: 'This booth runs an older build than the server; reloading at the next idle moment',
    data: { reason }
  })
  const tryReload = (): boolean => {
    if (!deps.isIdle()) return false
    try {
      deps.storage?.setItem(RELOAD_MARK_KEY, String(deps.now()))
    } catch {
      // see above
    }
    deps.reload()
    return true
  }
  if (!tryReload()) {
    const handle = deps.every(() => {
      if (tryReload()) deps.stop(handle)
    }, IDLE_POLL_MS)
  }
  return true
}

/** Tests only. */
export function resetStaleBuildForTests(): void {
  scheduled = false
}
